import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import tasksService from "../../services/tasks";
import TaskDetailsModal from "../../Components/TaskDetailsModal/TaskDetailsModal";
import "./Tasks.css";

export default function Tasks() {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [tasks, setTasks] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    page: 1,
    limit: 10
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState(null);

  // Date range filter state
  const [dateFilter, setDateFilter] = useState('all');
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [customFromDate, setCustomFromDate] = useState('');
  const [customToDate, setCustomToDate] = useState('');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const dateDropdownRef = useRef(null);

  // Sorting state
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const sortDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(event.target)) {
        setShowDateDropdown(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setShowSortDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Helper function to get date range based on filter
  const getDateRange = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (dateFilter) {
      case 'today': {
        const endOfDay = new Date(today);
        endOfDay.setHours(23, 59, 59, 999);
        return { from: today.toISOString(), to: endOfDay.toISOString() };
      }
      case '7days': {
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);
        const endOfDay = new Date(today);
        endOfDay.setHours(23, 59, 59, 999);
        return { from: sevenDaysAgo.toISOString(), to: endOfDay.toISOString() };
      }
      case 'month': {
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfDay = new Date(today);
        endOfDay.setHours(23, 59, 59, 999);
        return { from: startOfMonth.toISOString(), to: endOfDay.toISOString() };
      }
      case 'custom': {
        if (customFromDate && customToDate) {
          const from = new Date(customFromDate);
          from.setHours(0, 0, 0, 0);
          const to = new Date(customToDate);
          to.setHours(23, 59, 59, 999);
          return { from: from.toISOString(), to: to.toISOString() };
        }
        return { from: undefined, to: undefined };
      }
      default:
        return { from: undefined, to: undefined };
    }
  }, [dateFilter, customFromDate, customToDate]);

  // Get display label for date filter
  const getDateFilterLabel = () => {
    switch (dateFilter) {
      case 'today': return 'Today';
      case '7days': return 'Last 7 Days';
      case 'month': return 'This Month';
      case 'custom': return customFromDate && customToDate ? `${customFromDate} - ${customToDate}` : 'Custom Range';
      default: return 'All Time';
    }
  };

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const statusMap = {
        'all': undefined,
        'completed': 'Delivered',
        'ongoing': 'Assigned,In Transit',
        'pending': 'Pending'
      };

      const dateRange = getDateRange();

      const filters = {
        page: currentPage,
        limit: 10,
        status: statusMap[activeFilter],
        search: searchQuery || undefined,
        from: dateRange.from,
        to: dateRange.to,
        sortBy: sortBy,
        order: sortOrder
      };

      const result = await tasksService.getTasks(filters);
      setTasks(result.items || result.jobs || []);
      setPagination(result.pagination || { total: 0, totalPages: 0, page: 1, limit: 10 });
    } catch (err) {
      setError(err.message || 'Failed to load tasks');
      console.error('Tasks error:', err);
    } finally {
      setLoading(false);
    }
  }, [activeFilter, currentPage, searchQuery, getDateRange, sortBy, sortOrder]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const getStatusType = (status) => {
    const map = {
      'Delivered': 'delivered',
      'Assigned': 'assigned',
      'In Transit': 'assigned',
      'Pending': 'unassigned',
      'Exception': 'unassigned',
      'Returned': 'unassigned'
    };
    return map[status] || 'unassigned';
  };

  const formatDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Determine if task is in pre-pickup or post-pickup phase
  const isPrePickup = (status) => {
    return ['Pending', 'Approved', 'Assigned'].includes(status);
  };

  // Get the appropriate window based on status with label
  const getTimeWindow = (task) => {
    if (isPrePickup(task.status)) {
      // Show pickup window before pickup
      if (task.pickupWindow?.start && task.pickupWindow?.end) {
        return { time: `${task.pickupWindow.start}-${task.pickupWindow.end}`, label: 'pickup' };
      }
      // Fallback to delivery window if no pickup window
      if (task.deliveryWindow?.start && task.deliveryWindow?.end) {
        return { time: `${task.deliveryWindow.start}-${task.deliveryWindow.end}`, label: 'delivery' };
      }
    } else {
      // Show delivery window after pickup
      if (task.deliveryWindow?.start && task.deliveryWindow?.end) {
        return { time: `${task.deliveryWindow.start}-${task.deliveryWindow.end}`, label: 'delivery' };
      }
    }
    return null;
  };

  // Get ETA based on status
  const getETA = (task) => {
    if (task.estimatedArrival) {
      return formatDate(task.estimatedArrival);
    }
    if (isPrePickup(task.status)) {
      return formatDate(task.pickupDate);
    } else {
      return formatDate(task.deliveryDate);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
    }
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handleDateFilterChange = (filter) => {
    if (filter === 'custom') {
      setShowCustomDatePicker(true);
      setShowDateDropdown(false);
    } else {
      setDateFilter(filter);
      setShowDateDropdown(false);
      setShowCustomDatePicker(false);
      setCurrentPage(1);
    }
  };

  const handleCustomDateApply = () => {
    if (customFromDate && customToDate) {
      setDateFilter('custom');
      setShowCustomDatePicker(false);
      setShowDateDropdown(false);
      setCurrentPage(1);
    }
  };

  const handleCustomDateCancel = () => {
    setShowCustomDatePicker(false);
    setCustomFromDate('');
    setCustomToDate('');
  };

  // Sorting options
  const sortOptions = [
    { value: 'createdAt', label: 'Created Date' },
    { value: 'jobId', label: 'Task ID' },
    { value: 'scheduledDate', label: 'Scheduled Date' },
    { value: 'status', label: 'Status' },
    { value: 'priority', label: 'Priority' }
  ];

  const getSortLabel = () => {
    const option = sortOptions.find(opt => opt.value === sortBy);
    const orderLabel = sortOrder === 'desc' ? '(Newest)' : '(Oldest)';
    return `${option?.label || 'Created Date'} ${orderLabel}`;
  };

  const handleSortChange = (newSortBy) => {
    if (newSortBy === sortBy) {
      // Toggle order if same field
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc'); // Default to newest first
    }
    setShowSortDropdown(false);
    setCurrentPage(1);
  };

  const handleViewDetails = (taskId) => {
    setSelectedTaskId(taskId);
  };

  const handleCloseModal = () => {
    setSelectedTaskId(null);
  };

  return (
    <div className="tasks-page-container">
      {/* Welcome text */}
      <section className="mq-dashboard-header">
        <div>
          <p className="mq-dashboard-welcome-label">Welcome Back,</p>
          <h1 className="mq-dashboard-welcome-name">Tasks</h1>
        </div>

        <button
          className="mq-btn mq-dashboard-create-btn"
          onClick={() => navigate("/add-task")}
        >
          Add a Task +
        </button>
      </section>

      {error && (
        <div style={{
          padding: '12px',
          marginBottom: '20px',
          backgroundColor: '#fee',
          border: '1px solid #fcc',
          borderRadius: '4px',
          color: '#c33',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      {/* Main card */}
      <div className="tasks-card-wrapper">
        {/* Header: title + search + Today + Create Job */}
        <div className="tasks-header-section">
          <div className="tasks-header-top">
            <div className="tasks-header-left">
              <h2 className="tasks-header-title">Showing {activeFilter === 'all' ? 'All' : activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1)} Tasks</h2>
            </div>

            <div className="tasks-header-right">
              {/* search pill */}
              <div className="tasks-search-box">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="6.5"
                    cy="6.5"
                    r="5"
                    stroke="#9ca3af"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M10 10L14 14"
                    stroke="#9ca3af"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                <input
                  type="text"
                  className="tasks-search-input"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
                <span className="tasks-search-shortcut">Ctrl K</span>
              </div>

              {/* Sort dropdown */}
              <div className="tasks-sort-filter-wrapper" ref={sortDropdownRef}>
                <button
                  className="tasks-btn-sort"
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                >
                  Sort: {getSortLabel()} <span className="tasks-caret">▾</span>
                </button>

                {showSortDropdown && (
                  <div className="tasks-sort-dropdown">
                    {sortOptions.map(option => (
                      <button
                        key={option.value}
                        className={`tasks-sort-option ${sortBy === option.value ? 'active' : ''}`}
                        onClick={() => handleSortChange(option.value)}
                      >
                        {option.label}
                        {sortBy === option.value && (
                          <span className="tasks-sort-arrow">
                            {sortOrder === 'desc' ? ' ↓' : ' ↑'}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Date filter dropdown */}
              <div className="tasks-date-filter-wrapper" ref={dateDropdownRef}>
                <button
                  className={`tasks-btn-today ${dateFilter !== 'all' ? 'active' : ''}`}
                  onClick={() => setShowDateDropdown(!showDateDropdown)}
                >
                  {getDateFilterLabel()} <span className="tasks-caret">▾</span>
                </button>

                {showDateDropdown && (
                  <div className="tasks-date-dropdown">
                    <button
                      className={`tasks-date-option ${dateFilter === 'all' ? 'active' : ''}`}
                      onClick={() => handleDateFilterChange('all')}
                    >
                      All Time
                    </button>
                    <button
                      className={`tasks-date-option ${dateFilter === 'today' ? 'active' : ''}`}
                      onClick={() => handleDateFilterChange('today')}
                    >
                      Today
                    </button>
                    <button
                      className={`tasks-date-option ${dateFilter === '7days' ? 'active' : ''}`}
                      onClick={() => handleDateFilterChange('7days')}
                    >
                      Last 7 Days
                    </button>
                    <button
                      className={`tasks-date-option ${dateFilter === 'month' ? 'active' : ''}`}
                      onClick={() => handleDateFilterChange('month')}
                    >
                      This Month
                    </button>
                    <button
                      className={`tasks-date-option ${dateFilter === 'custom' ? 'active' : ''}`}
                      onClick={() => handleDateFilterChange('custom')}
                    >
                      Custom Range
                    </button>
                  </div>
                )}

                {showCustomDatePicker && (
                  <div className="tasks-custom-date-picker">
                    <div className="tasks-custom-date-header">Select Date Range</div>
                    <div className="tasks-custom-date-inputs">
                      <div className="tasks-custom-date-field">
                        <label>From</label>
                        <input
                          type="date"
                          value={customFromDate}
                          onChange={(e) => setCustomFromDate(e.target.value)}
                          max={customToDate || undefined}
                        />
                      </div>
                      <div className="tasks-custom-date-field">
                        <label>To</label>
                        <input
                          type="date"
                          value={customToDate}
                          onChange={(e) => setCustomToDate(e.target.value)}
                          min={customFromDate || undefined}
                        />
                      </div>
                    </div>
                    <div className="tasks-custom-date-actions">
                      <button
                        className="tasks-custom-date-cancel"
                        onClick={handleCustomDateCancel}
                      >
                        Cancel
                      </button>
                      <button
                        className="tasks-custom-date-apply"
                        onClick={handleCustomDateApply}
                        disabled={!customFromDate || !customToDate}
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Filter tabs strip */}
          <div className="tasks-filter-strip">
            <button
              className={`tasks-filter-tab ${
                activeFilter === "all" ? "active" : ""
              }`}
              onClick={() => handleFilterChange("all")}
            >
              All
            </button>
            <button
              className={`tasks-filter-tab ${
                activeFilter === "completed" ? "active" : ""
              }`}
              onClick={() => handleFilterChange("completed")}
            >
              Completed
            </button>
            <button
              className={`tasks-filter-tab ${
                activeFilter === "ongoing" ? "active" : ""
              }`}
              onClick={() => handleFilterChange("ongoing")}
            >
              Ongoing
            </button>
            <button
              className={`tasks-filter-tab ${
                activeFilter === "pending" ? "active" : ""
              }`}
              onClick={() => handleFilterChange("pending")}
            >
              Pending
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="tasks-table-wrapper">
        <table className="tasks-data-table">
          <thead>
            <tr className="tasks-first">
              <th>Tasks ID</th>
              <th>From → To</th>
              <th>Window</th>
              <th>ETA</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#6366f1' }}>
                  Loading tasks...
                </td>
              </tr>
            ) : tasks.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                  No tasks found
                </td>
              </tr>
            ) : (
              tasks.map((task) => {
                const statusType = getStatusType(task.status);
                return (
                  <tr
                    key={task._id}
                    className="tasks-table-row"
                    onClick={() => handleViewDetails(task._id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="tasks-col-id">{task.jobId}</td>
                    <td className="tasks-col-route">
                      <span className="tasks-location-badge tasks-from">
                        {task.from?.name || 'N/A'}
                      </span>
                      <span className="tasks-location-arrow">→</span>
                      <span className="tasks-location-badge tasks-to">
                        {task.to?.name || 'N/A'}
                      </span>
                    </td>
                    <td className="tasks-col-window">
                      {(() => {
                        const windowData = getTimeWindow(task);
                        return windowData
                          ? <>{windowData.time} <span className="tasks-window-label">({windowData.label})</span></>
                          : 'N/A';
                      })()}
                    </td>
                    <td className="tasks-col-eta">
                      {getETA(task) || 'N/A'}
                    </td>
                    <td className="tasks-col-status">
                      {statusType === "delivered" && (
                        <span className="tasks-status-delivered">
                          {task.status}
                        </span>
                      )}
                      {statusType === "assigned" && (
                        <span className="tasks-status-assigned">{task.status}</span>
                      )}
                      {statusType === "unassigned" && (
                        <span className="tasks-status-unassigned">
                          {task.status}
                        </span>
                      )}
                    </td>
                    <td
                      className="tasks-col-menu"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetails(task._id);
                      }}
                    >
                      👁️
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>

        {/* Footer / pagination */}
        <div className="tasks-footer-section">
          <span className="tasks-footer-text">
            Page {pagination.page || 1} of {pagination.totalPages || 1}
          </span>
          <div className="tasks-pagination-controls">
            <button
              className="tasks-pagination-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              ‹ Prev
            </button>

            {currentPage > 1 && (
              <button
                className="tasks-pagination-btn"
                onClick={() => handlePageChange(1)}
              >
                1
              </button>
            )}

            <button className="tasks-pagination-btn tasks-pagination-active">
              {currentPage}
            </button>

            {currentPage < pagination.totalPages && (
              <button
                className="tasks-pagination-btn"
                onClick={() => handlePageChange(pagination.totalPages)}
              >
                {pagination.totalPages}
              </button>
            )}

            <button
              className="tasks-pagination-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === pagination.totalPages}
            >
              Next ›
            </button>
          </div>
        </div>
      </div>

      {/* Task Details Modal */}
      {selectedTaskId && (
        <TaskDetailsModal
          taskId={selectedTaskId}
          onClose={handleCloseModal}
          onUpdate={fetchTasks}
        />
      )}
    </div>
  );
}
